import styled from "styled-components";

const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Section = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(72px, 9vw, 120px) 0;
  background: ${(p) => p.$bg || "#ffffff"};
  border-top: ${(p) => (p.$divider ? "1px solid #f1f5f9" : "none")};
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const Heading = styled.h2`
  font-size: clamp(32px, 4.2vw, 52px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const Lead = styled.p`
  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto;
  max-width: 620px;
`;

/* Pull up under the global Section margin in main.js so the hero
   background reaches the fixed header. Add the same amount back as
   padding-top so visual spacing stays the same. */
const Hero = styled.section`
  display: block;
  min-height: auto;
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(72px, 9vw, 120px);
  text-align: center;
`;

const HeroInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(44px, 6.5vw, 84px);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.04em;
  color: #0f172a;
  margin: 0 0 28px;
`;

const HeroLead = styled.p`
  font-size: clamp(17px, 1.6vw, 21px);
  line-height: 1.55;
  color: #64748b;
  margin: 0 auto 40px;
  max-width: 560px;
`;

const CTARow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  background: #0f172a;
  color: #ffffff;
  font-weight: 500;
  font-size: 15px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #1e293b;
    color: #ffffff;
  }
  &:active {
    transform: translateY(1px);
  }
`;

const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 14px 20px;
  background: transparent;
  color: #475569;
  font-weight: 500;
  font-size: 15px;
  border-radius: 10px;
  text-decoration: none;
  border: 1px solid #e2e8f0;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    color: #0f172a;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 56px;
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #e2e8f0;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Cell = styled.div`
  background: #ffffff;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    background: #eef2ff;
    color: #4f46e5;
  }
  h3 {
    font-size: 17px;
    font-weight: 600;
    color: #0f172a;
    margin: 4px 0 0;
    letter-spacing: -0.01em;
  }
  p {
    font-size: 14.5px;
    line-height: 1.6;
    color: #64748b;
    margin: 0;
  }
`;

const Specs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  padding: 40px 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }
`;

const Spec = styled.div`
  text-align: left;

  .value {
    font-size: clamp(28px, 3.5vw, 38px);
    font-weight: 700;
    letter-spacing: -0.025em;
    color: #0f172a;
    line-height: 1;
  }
  .label {
    margin-top: 8px;
    font-size: 14px;
    color: #64748b;
  }
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  img {
    width: 100%;
    border-radius: 16px;
    display: block;
  }
`;

const FinalCTA = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(96px, 12vw, 140px) 24px;
  text-align: center;
  background: #fafafa;
  border-top: 1px solid #f1f5f9;
`;

// Inline SVG icons — single-stroke, minimal
const IconMemory = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 9V3M12 21v-6M9 12H3M21 12h-6" />
  </svg>
);
const IconAttention = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </svg>
);
const IconExec = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);
const IconLang = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconVisuo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" />
  </svg>
);
const IconCamera = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const areas = [
  { Icon: IconMemory, title: "Memory", desc: "Short-term and working memory through pattern recall and sequence matching." },
  { Icon: IconAttention, title: "Attention", desc: "Sustained focus through tracking, reaction-time, and visual filtering activities." },
  { Icon: IconExec, title: "Executive Function", desc: "Planning, problem-solving, and mental flexibility through strategy play." },
  { Icon: IconLang, title: "Language", desc: "Verbal fluency and word retrieval through vocabulary and word-finding tasks." },
  { Icon: IconVisuo, title: "Visuospatial", desc: "Spatial reasoning and visual processing through pattern and assembly tasks." },
  { Icon: IconCamera, title: "Motion-Based", desc: "Full-body interaction via standard webcam — no wearables, no controllers." },
];

export function Cogame() {
  return (
    <Page>
      <Hero>
        <HeroInner>
          <HeroTitle>
            Keep minds
            <br />
            active and sharp.
          </HeroTitle>
          <HeroLead>
            Cogame is an AI motion and voice based cognitive engagement
            platform for senior care centers, developed from the Brain Health
            Playground research program.
          </HeroLead>
          <CTARow>
            <PrimaryButton
              href="https://cogame.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit cogame.ai
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </PrimaryButton>
            <GhostButton href="#features">Learn more</GhostButton>
          </CTARow>
        </HeroInner>
      </Hero>

      <Section id="features" $divider>
        <Container>
          <SectionHeader>
            <Eyebrow>Comprehensive training</Eyebrow>
            <Heading>Built around the way the brain works.</Heading>
            <Lead>
              Every game targets a specific cognitive function. Together they
              cover the core domains that matter most for cognitive wellness in
              older adults.
            </Lead>
          </SectionHeader>

          <Grid>
            {areas.map(({ Icon, title, desc }) => (
              <Cell key={title}>
                <div className="icon">
                  <Icon />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </Cell>
            ))}
          </Grid>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader>
            <Eyebrow>What makes it different</Eyebrow>
            <Heading>Brain training that moves the body.</Heading>
            <Lead>
              Most cognitive apps live on phones and tablets — tap, swipe,
              type. Cogame uses a standard webcam to detect full-body motion,
              pairing cognitive activity with natural physical engagement.
            </Lead>
          </SectionHeader>

          <Specs>
            <Spec>
              <div className="value">20+</div>
              <div className="label">Brain games</div>
            </Spec>
            <Spec>
              <div className="value">5</div>
              <div className="label">Cognitive areas</div>
            </Spec>
            <Spec>
              <div className="value">100%</div>
              <div className="label">Motion-based</div>
            </Spec>
            <Spec>
              <div className="value">0</div>
              <div className="label">Extra devices</div>
            </Spec>
          </Specs>
        </Container>
      </Section>

      <FinalCTA>
        <Container>
          <Heading style={{ marginBottom: 16 }}>See Cogame in action.</Heading>
          <Lead style={{ margin: "0 auto 32px" }}>
            Explore games, research, and pricing for senior care centers on
            the full Cogame site.
          </Lead>
          <CTARow>
            <PrimaryButton
              href="https://cogame.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open cogame.ai
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </PrimaryButton>
          </CTARow>
        </Container>
      </FinalCTA>
    </Page>
  );
}
